import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { SelectField, InputField } from "@/components/ui/FormFields";
import { Modal } from "@/components/ui/Modal";
import {
  listInterestsQueue,
  approveInterestProposal,
  rejectInterestProposal,
  type InterestProposal,
  type InterestRejectReason,
} from "@/api/admin";

export default function InterestsQueueLab() {
  const qc = useQueryClient();

  // The queue endpoint always returns PENDING proposals (status is server-fixed),
  // so there is no status filter to offer.
  const { data, isLoading } = useQuery({
    queryKey: ["admin-interests-queue"],
    queryFn: () => listInterestsQueue({ limit: 50 }),
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["admin-interests-queue"] });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Interests Queue"
        subtitle="User-submitted interest proposals awaiting review (Phase 1A)."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Interests" },
        ]}
      />

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.items.length === 0 ? (
        <p className="text-sm text-gray-500">No proposals.</p>
      ) : (
        <ul className="space-y-2">
          {data.data.items.map((p) => (
            <ProposalRow key={p.id} proposal={p} onChange={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ProposalRow({
  proposal,
  onChange,
}: {
  proposal: InterestProposal;
  onChange: () => void;
}) {
  const [modal, setModal] = useState<"approve" | "reject" | null>(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState<InterestRejectReason>("inappropriate");

  async function handleApprove() {
    setBusy(true);
    try {
      await approveInterestProposal(proposal.id, notes || undefined);
      onChange();
      close();
    } finally {
      setBusy(false);
    }
  }

  // `details` is required by the server when reason is "other".
  const rejectDisabled = reason === "other" && notes.trim().length === 0;

  async function handleReject() {
    if (rejectDisabled) return;
    setBusy(true);
    try {
      await rejectInterestProposal(proposal.id, {
        reason,
        details: notes.trim() || undefined,
      });
      onChange();
      close();
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setModal(null);
    setNotes("");
    setReason("inappropriate");
  }

  const canAct = proposal.status === "pending";

  return (
    <li className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">
            {proposal.emoji ? `${proposal.emoji} ` : ""}
            {proposal.canonicalNameEn}
          </span>
          <Badge
            variant={
              proposal.status === "approved"
                ? "green"
                : proposal.status === "rejected"
                  ? "red"
                  : "yellow"
            }
          >
            {proposal.status}
          </Badge>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          <code className="font-mono">{proposal.code}</code> ·{" "}
          {proposal.interestType} · {proposal.category}
          {proposal.proposalCount && proposal.proposalCount > 1 && (
            <> · {proposal.proposalCount} proposals (auto-approve threshold)</>
          )}
        </div>
        {proposal.rejectReason && (
          <p className="text-xs text-red-700 mt-1">
            Reason: {proposal.rejectReason}
          </p>
        )}
      </div>
      {canAct && (
        <div className="flex gap-1 shrink-0">
          <Button size="sm" onClick={() => setModal("approve")}>
            Approve
          </Button>
          <Button size="sm" variant="danger" onClick={() => setModal("reject")}>
            Reject
          </Button>
        </div>
      )}

      <Modal
        isOpen={modal === "approve"}
        onClose={close}
        title={`Approve "${proposal.canonicalNameEn}"`}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={handleApprove} isLoading={busy}>
              Approve
            </Button>
          </>
        }
      >
        <InputField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Modal>

      <Modal
        isOpen={modal === "reject"}
        onClose={close}
        title={`Reject "${proposal.canonicalNameEn}"`}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              isLoading={busy}
              disabled={rejectDisabled}
            >
              Reject
            </Button>
          </>
        }
      >
        <SelectField
          label="Reason"
          required
          value={reason}
          onChange={(e) =>
            setReason(e.target.value as InterestRejectReason)
          }
          options={[
            { value: "inappropriate", label: "Inappropriate" },
            { value: "duplicate_existing", label: "Duplicate of existing" },
            { value: "too_vague", label: "Too vague" },
            { value: "out_of_scope", label: "Out of scope" },
            { value: "other", label: "Other" },
          ]}
        />
        <InputField
          label={reason === "other" ? "Details (required)" : "Details (optional)"}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Modal>
    </li>
  );
}
